import React, { useEffect, forwardRef, RefObject } from 'react';
import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import VIDEO_EVENTS from 'constants/video-events';

export const StyledVideoPlayer = styled.video`
  width: ${({ width }) => scaleSize(width)}px;
  height: ${({ height }) => scaleSize(height)}px;
`;

interface IVideoComponentProps {
  width: number;
  height: number;
  onEvent: (type: string) => void;
  forwardedRef: RefObject<HTMLVideoElement>;
}

const VideoComponent: React.FC<IVideoComponentProps> = (props) => {
  const { width, height, onEvent, forwardedRef, children } = props;

  useEffect(() => {
    const video = forwardedRef.current;

    const events = {
      timeupdate: () => onEvent(VIDEO_EVENTS.TIME_UPDATE),
      loadedmetadata: () => onEvent(VIDEO_EVENTS.LOADED_METADATA),
      play: () => onEvent(VIDEO_EVENTS.PLAY),
      pause: () => onEvent(VIDEO_EVENTS.PAUSE),
      waiting: () => onEvent(VIDEO_EVENTS.WAITING),
      seeking: () => onEvent(VIDEO_EVENTS.SEEKING),
      seeked: () => onEvent(VIDEO_EVENTS.SEEKED),
      loadstart: () => onEvent(VIDEO_EVENTS.LOAD_START),
      canplay: () => onEvent(VIDEO_EVENTS.CAN_PLAY),
      playing: () => onEvent(VIDEO_EVENTS.PLAYING),
      ended: () => onEvent(VIDEO_EVENTS.ENDED),
    };

    Object.entries(events).forEach(([key, value]) => video.addEventListener(key, value));

    return () => {
      Object.entries(events).forEach(([key, value]) => video.removeEventListener(key, value));
    };
  }, [onEvent, forwardedRef]);

  // it's willUnmount.
  // For track cases when player has stopped without "stop" button press. E.g. with back button
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => onEvent(VIDEO_EVENTS.PAUSE), []);

  return (
    <StyledVideoPlayer width={width} height={height} ref={forwardedRef}>
      {children}
    </StyledVideoPlayer>
  );
};

export default forwardRef(
  (props: Omit<IVideoComponentProps, 'forwardedRef'>, ref: RefObject<HTMLVideoElement>) => (
    <VideoComponent forwardedRef={ref} {...props} />
  ),
);
